import React, { useEffect } from 'react';
import { Modal, Form, Input, Switch, message, ColorPicker, Select } from 'antd';
import { updateVocabularyList } from '@/libs/api';
import type { components } from '@/api/types';

type VocabularyListDto = components['schemas']['VocabularyListDto'];
interface EditVocabularyModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  book: VocabularyListDto | null;
}

const DEFAULT_THEME_COLOR = '#6a0dad';
const MAX_TAGS = 5;

// 预设的主题颜色
const PRESET_COLORS = {
  recommended: [
    '#6a0dad', // 主色紫色
    '#b555fa', // 次要紫色
    '#eee1f8', // 浅紫色
    '#52C41A', // 绿色
    '#FAAD14', // 黄色
    '#F5222D', // 红色
    '#13C2C2', // 青色
    '#FA8C16', // 橙色
    '#666666', // 灰色
    '#000000', // 黑色
  ],
};

const EditVocabularyModal: React.FC<EditVocabularyModalProps> = ({
  open,
  onCancel,
  onSuccess,
  book,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (book) {
      form.setFieldsValue({
        name: book.name,
        isPublic: book.isPublic,
        themeColor: book.themeColor || DEFAULT_THEME_COLOR,
        tags: book.tags || [],
      });
    }
  }, [book, form]);

  const handleSubmit = async () => {
    if (!book) return;
    try {
      const values = await form.validateFields();
      setLoading(true);
      await updateVocabularyList(book.id, {
        name: values.name,
        isPublic: values.isPublic,
        themeColor: values.themeColor?.toHexString() || DEFAULT_THEME_COLOR,
        tags: values.tags || [],
      });
      message.success('更新成功');
      form.resetFields();
      onSuccess();
      onCancel();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('更新失败');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="编辑生词本"
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="生词本名称"
          rules={[{ required: true, message: '请输入生词本名称' }]}
        >
          <Input placeholder="请输入生词本名称" />
        </Form.Item>

        <Form.Item name="themeColor" label="主题颜色">
          <ColorPicker
            presets={[
              {
                label: '推荐颜色',
                colors: PRESET_COLORS.recommended,
              },
            ]}
            size="large"
            showText
            disabledAlpha
            format="hex"
          />
        </Form.Item>

        <Form.Item name="tags" label="标签">
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="输入标签后按回车"
            maxTagCount={MAX_TAGS}
            maxTagTextLength={10}
          />
        </Form.Item>

        <Form.Item name="isPublic" label="是否公开" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditVocabularyModal;
